import { useState, useEffect, RefObject } from "react";
import { FUNNEL_MEASUREMENT_DELAY_MS } from "../components/slideInMenu/constants";

interface UseContentHeightMeasurementOptions {
  /**
   * Ref to the element whose height should be measured
   */
  contentRef: RefObject<HTMLDivElement>;

  /**
   * Dependencies that trigger re-measurement
   */
  dependencies: readonly unknown[];

  /**
   * Whether measurement should be enabled
   */
  enabled: boolean;

  /**
   * Optional offset to add to measured height (e.g., for borders)
   */
  heightOffset?: number;

  /**
   * Number of requestAnimationFrame calls to wait before measuring
   * Defaults to 2 for maximum stability
   */
  rafIterations?: number;
}

/**
 * Hook to measure content height with proper timing for animated transitions.
 *
 * Animation sequence:
 * 1. Old content fades out (200ms) via AnimatePresence mode="wait"
 * 2. After FUNNEL_MEASUREMENT_DELAY_MS: New content mounts and we start observing its height
 * 3. New content fades in + container resizes (both 200ms, simultaneous)
 *
 * The delay (210ms = 200ms animation + 10ms buffer) ensures the exit animation
 * completes before measurement, so we measure the NEW content's height, not the old.
 *
 * Uses ResizeObserver to continuously track height changes, which handles:
 * - Slow API responses that load content after initial mount
 * - Dynamic content that changes size (images loading, expanding sections, etc.)
 * - Font loading that affects text height
 *
 * Multiple requestAnimationFrame calls before initial observation ensure the browser
 * has fully laid out the new content, preventing stale measurements.
 */
export function useContentHeightMeasurement({
  contentRef,
  dependencies,
  enabled,
  heightOffset = 10,
  rafIterations = 1,
}: UseContentHeightMeasurementOptions): number | null {
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || !contentRef.current) {
      setContentHeight(null);
      return;
    }

    let resizeObserver: ResizeObserver | null = null;

    const timer = setTimeout(() => {
      // Chain requestAnimationFrame calls based on rafIterations
      let currentIteration = 0;

      const startObservingAfterRaf = () => {
        currentIteration++;

        if (currentIteration < rafIterations) {
          requestAnimationFrame(startObservingAfterRaf);
          return;
        }

        // Start observing after all RAF iterations
        if (contentRef.current) {
          resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
              // Use borderBoxSize for more accurate measurements
              const height = entry.target.scrollHeight;
              const finalHeight = height + heightOffset;
              setContentHeight(finalHeight);
            }
          });

          resizeObserver.observe(contentRef.current);
        }
      };

      requestAnimationFrame(startObservingAfterRaf);
    }, FUNNEL_MEASUREMENT_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [...dependencies, enabled]);

  return contentHeight;
}
